import React, { useState } from 'react';
import {
  CertificateInput,
  ExcelRow,
  IAddCertificateDrawerProps,
  Production_CertificateDetailsOutputInput,
} from '.';
import { inputs } from './content';
import { toast } from 'react-toastify';
import { useMutation } from '@apollo/client';
import { UPLOAD_CERTIFICATE_MUTATION } from './queries';
import { EnhancedButtonStatus } from '@components/_old/EnhancedButton';
import { excelToJson } from '@utils/excel-reader';
import ToastErrorMessage from '@components/toast/ToastErrorMessage';
import DateService from '@services/DateService';
import { isEmpty } from '@utils/validationUtils';
import ToastSuccessMessage from '@components/toast/ToastSuccessMessage';
import { getError } from '@utils/graph-utils';
import { DEFAULT_ERROR_TEXT } from '@utils';
import GenericDrawer from '@components/generic-drawer/GenericDrawer';
import DynamicForm from '@components/_old/dynamicForm/DynamicForm';
import PolicyCertficateMedicalGroupSummaryDialogShell from '../medical-group-certificate-popup/PolicyCertficateMedicalGroupSummaryDialogShell';

const AddMedicalCertificateDrawer: React.FC<IAddCertificateDrawerProps> = ({
  id,
  open,
  onSuccess,
  onClose,
  planDetails,
}) => {
  const [formDisabled, setFormDisabled] = useState(false);
  const [submitButtonState, setSubmitButtonState] =
    useState<EnhancedButtonStatus>();
  const [summaryData, setSummaryData] =
    useState<Production_CertificateDetailsOutputInput | null>(null);

  const [uploadCertificates] = useMutation(UPLOAD_CERTIFICATE_MUTATION);

  const simulateDataUpload = async (file: File): Promise<void> => {
    setSubmitButtonState('loading');
    setFormDisabled(true);

    try {
      const excelData = await excelToJson(file);

      // Assuming data is in the first sheet
      const sheetNames = Object.keys(excelData);
      if (sheetNames.length === 0) {
        toast.error(<ToastErrorMessage>{'Invalid file'}</ToastErrorMessage>);
        return;
      }

      const firstSheetName = sheetNames[0];
      const rows = excelData[firstSheetName];
      const certificateInputs: CertificateInput[] = rows
        .map((excelRow: ExcelRow, index: number) => {
          // Remove trailing '*' from keys
          const row = Object.fromEntries(
            Object.entries(excelRow).map(([key, value]) => [
              key.replace(/\*$/, '').trim(),
              value,
            ])
          );

          return {
            rowNumber: index + 1,
            reference: row['Reference']?.toString() || null,
            firstName: row['First Name'] || null,
            middleName: row['Middle Name'] || null,
            lastName: row['Last Name'] || null,
            dateOfBirth:
              DateService.formatDateBackendWithFormat(row['Date of Birth']) ||
              null,
            gender: row['Gender']?.toUpperCase() || null,
            principalReference: row['Principal Reference']?.toString() || null,
            relation: row['Relation']?.toUpperCase() || null,
            class: row['Class'] || null,
            nssf: row['Co-NSSF']?.toUpperCase() || null,
            level: row['Level'] || null,
            address: row['Address'] || null,
            phone: `+${row['Phone']?.toString()}` || null,
            exclusions: row['Exclusion/Limitation'] || null,
            remarks: row['Remarks'] || null,
            continuity: row['Continuity'] || null,
            grApplicable: row['GR Applicable']?.toUpperCase() === 'YES' || null,
            tpaReference1: row['TPA Reference1'] || null,
            tpaReference2: row['TPA Reference2'] || null,
            effectiveDate:
              DateService.formatDateBackendWithFormat(row['Effective Date']) ||
              null,
          };
        })
        .filter((row) => !isEmpty(row.reference));

      const { data, errors } = await uploadCertificates({
        variables: {
          entityId: id,
          medicalInput: certificateInputs,
        },
        errorPolicy: 'all',
      });

      if (isEmpty(errors)) {
        toast.success(
          <ToastSuccessMessage>
            {'Data successfully uploaded'}
          </ToastSuccessMessage>
        );

        const uploadResult =
          data?.production?.entities?.policy?.production
            ?.uploadMedicalCertificateInputs;

        const summaryResult = {
          Items: uploadResult.Items || [],
        };

        setSummaryData(summaryResult);
        setSubmitButtonState('success');
      } else {
        setSubmitButtonState(undefined);
        const errorMessage = getError({ errors });
        toast.error(<ToastErrorMessage>{errorMessage}</ToastErrorMessage>);
      }
    } catch (error) {
      setSubmitButtonState(undefined);
      toast.error(<ToastErrorMessage>{DEFAULT_ERROR_TEXT}</ToastErrorMessage>);
    } finally {
      setFormDisabled(false);
    }
  };

  const submitForm = async (values: Record<string, File>) => {
    await simulateDataUpload(values.document);
  };

  const handleSummaryClose = () => {
    setSummaryData(null);
    onClose();
  };

  const handleSummarySuccess = () => {
    setSummaryData(null);
    onSuccess();
    onClose();
  };

  return (
    <>
      <GenericDrawer title={'Add Certificates'} onClose={onClose} isOpen={open}>
        <DynamicForm
          inputs={inputs()}
          onSubmit={submitForm}
          buttonText={'Submit'}
          submitButtonState={submitButtonState}
          disableForm={formDisabled}
          title="Information"
        />
      </GenericDrawer>

      {summaryData && (
        <PolicyCertficateMedicalGroupSummaryDialogShell
          data={summaryData}
          entityId={id}
          planDetails={planDetails}
          onClose={handleSummaryClose}
          onSuccess={handleSummarySuccess}
        />
      )}
    </>
  );
};

export default AddMedicalCertificateDrawer;
